import React from 'react'
import classNames from 'classnames/bind'
import { useFormatMessage } from '@babylon/intl'
import { Alert, Link, Text } from '@babylon/medkit'
import { useSelector } from 'react-redux'
import { useQuery } from '@apollo/react-hooks'
import { miamiTheme, ThemeProvider } from '@babylon/web-platform-ui'
import Template from '../Template'
import messages from '../messages'
import {
  useWeightLossImmediatePayment,
  useWeightLossDiscountCode,
  useWeightLossDiscountCodeValidUntil,
  useWeightLossDiscountAmount,
  useWeightLossDiscountNewPrice,
} from '@/redux/selectors'
import styles from '../index.module.scss'
import { onboardingSteps } from '../Setup/steps'
import { getDiscountCode } from '@/hooks/useHandleDiscountCode'
import ProductsQuery from '@/queries/Products'
import { PLATFORM_GATEWAY_GRAPHQL_ENDPOINT } from '@/config'
import { Product, ProductType } from '@/programmes/components/Products/models'
import Products from '@/programmes/components/Products/Products'
import { setupRoutes } from '@/programmes/WeightLoss/routes'
import Spinner from '@/programmes/components/Spinner'

const cx = classNames.bind(styles)

const useGetDiscountParams = () => {
  // these are coming from Product Config
  const weightLossDiscountCode = useWeightLossDiscountCode()
  const weightLossDiscountCodeValidUntil = useWeightLossDiscountCodeValidUntil()
  const weightLossDiscountAmount = useWeightLossDiscountAmount()
  const weightLossDiscountNewPrice = useWeightLossDiscountNewPrice()

  // these are coming from Ads -> Marketing website
  const discountCodeParams = getDiscountCode()

  return {
    discountCodeId: discountCodeParams?.discount_code_id,
    discountCode: discountCodeParams?.discount_code || weightLossDiscountCode,
    discountAmount:
      discountCodeParams?.discount_amount || weightLossDiscountAmount,
    discountExpiry:
      discountCodeParams?.discount_expiry || weightLossDiscountCodeValidUntil,
    discountNewPrice:
      discountCodeParams?.discount_new_price || weightLossDiscountNewPrice,
  }
}

const CheckOutHome = () => {
  const t = useFormatMessage()
  const weightLossImmediatePayment = useWeightLossImmediatePayment()
  const {
    discountCode,
    discountCodeId,
    discountAmount,
    discountExpiry,
    discountNewPrice,
  } = useGetDiscountParams()

  const hasDiscountCode = discountCode && discountAmount && discountExpiry
  const userUUID = useSelector((state: any) => state.auth?.user?.uuid)
  const { data: productsData, loading: productsLoading } = useQuery(
    ProductsQuery,
    {
      fetchPolicy: 'network-only',
      context: { uri: PLATFORM_GATEWAY_GRAPHQL_ENDPOINT },
      variables: {
        patientId: userUUID,
        productGroups: ['weightLossMedication'],
      },
    }
  )

  if (productsLoading) {
    return (
      <div className={styles.spinner}>
        <Spinner colour="#000000" />
      </div>
    )
  }

  const productsFromApi: Product[] = productsData?.patient?.products?.items

  // This is a temporary solution that allows adding to the product type. It is necessary to complete the questionnaire data.
  const enhancedProducts: Product[] = productsFromApi.map((product) => {
    if (product.name.toLowerCase().includes('mounjaro')) {
      return {
        ...product,
        type: ProductType.Mounjaro,
      }
    }

    return {
      ...product,
      type: ProductType.Wegovy,
    }
  })

  return (
    <Template
      title={t(messages.checkoutPageTitle)}
      steps={onboardingSteps}
      activeStep={1}
    >
      <h2 className={cx('heading')}>{t(messages.checkoutPageHeading)}</h2>

      <ThemeProvider theme={miamiTheme}>
        {discountCode && discountAmount && discountExpiry && (
          <Alert variant="status" className={cx('discount-code-alert')}>
            <div>
              <span className={cx('discount-code-description')}>
                {t(messages.checkoutPageDiscountCodeText1)}{' '}
                <strong>{discountCode}</strong>
              </span>{' '}
              <span className={cx('discount-code-description')}>
                {t(messages.checkoutPageDiscountCodeText2)} {discountAmount}
              </span>
            </div>
            <div style={{ fontSize: '12px' }}>
              {t(messages.checkoutPageDiscountValidUntil)} {discountExpiry}
            </div>
          </Alert>
        )}
        <Products
          products={enhancedProducts}
          successPath={setupRoutes.gpInfo}
          cancelPath={setupRoutes.home}
          discountCodeId={discountCodeId}
        />
      </ThemeProvider>

      {weightLossImmediatePayment ? (
        <Text className={cx('small-text')} variant="smallBody">
          {t(messages.checkoutDescription3Immediate)}
        </Text>
      ) : (
        <>
          <Text className={cx('small-text')} variant="smallBody">
            {t(messages.checkoutDescription3Immediate)}
          </Text>
          <Text className={cx('small-text')} variant="smallBody">
            {t(messages.checkoutPageSmallPrint)}
          </Text>
        </>
      )}

      <Link className={cx('small-text')} id="subtitle" href="/terms" blank>
        {hasDiscountCode && discountNewPrice
          ? t(messages.checkoutAfterDiscountCodeTermsAndConditions)
          : t(messages.checkoutPageTermsAndConditions)}
      </Link>
      <Text className={cx('small-text')} variant="smallBody">
        {t(messages.checkoutMounjaroDisclaimer)}
      </Text>
    </Template>
  )
}

export default CheckOutHome
